import { useContext, useEffect, useState } from "react";
import { Box, colors, Container, makeStyles } from "@material-ui/core";
import { FormikHelpers, useFormik } from "formik";
import CurrencyFormat from "react-currency-format";
import clsx from "clsx";
import * as Yup from "yup";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Alert from "@material-ui/lab/Alert";
import Modal from "@material-ui/core/Modal";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import { InputField } from "../../../../../components/input-field/InputField";
import { InputButton } from "../../../../../components/input-button/InputButton";
import Account from "../../../../../models/Account";
import { PageTitle } from "../../../../../components/page-title/PageTitle";
import { AmountDetail } from "../../../../icip_admin_screen/components/AmountDetail";
import { OutlineButton } from "../../../../icip_admin_screen/components/Buttons";
import { displayWalletType } from "../../../../../settings/WalletType";
import Business from "../../../../../models/Business";
import BusinessService from "../../../../../services/BusinessService";
import { AlertTitle } from "@material-ui/lab";
import { ICIPAdminActionTypeEnum, ICIPAdminDispatchContext } from "../../../../../context/ICIPAdminContext";
import { RestApiResponse } from "../../../../../services/RestApi";
import { BaseResponseAttributes } from "../../../../../attributes/base-attributes";

interface NewWithdrawRequestProps {
  business: Business,
  isOpened: boolean;
  callback: (succeed: boolean) => void;
}

const useStyles = makeStyles(
  (theme) => ({
    root: {},
    wrapper: {
      "& h4": {
        fontWeight: theme.typography.fontWeightBold,
      },

      "& input": {
        fontSize: 18,
        fontWeight: theme.typography.fontWeightBold,
      },
    },

    subTitle: {
      display: "block",
      fontSize: 13,
      color: colors.grey[600],
      marginTop: theme.spacing(2),
    },

    walletItems: {},

    walletItemsGrid: {},

    walletItem: {
      border: "1px solid #f2f2f2",
      backgroundColor: "#fafafa",
      padding: "4px 18px",
      cursor: "pointer",
    },

    walletItemCurrent: {
      border: `1px solid ${theme.palette.secondary.main}`,
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.common.white,

      "& span": {
        color: theme.palette.common.white,
      },
    },

    error: {
      fontSize: 13,
      color: colors.red[700],
      textAlign: "left",
    },

    loader: {
      marginTop: 8,
    },

    currency: {
      fontSize: 18,
      color: theme.palette.text.primary,
      fontWeight: theme.typography.fontWeightBold,
    },
    modal: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    modalTitle: {
      fontSize: 20,
      fontWeight: "bold",
    },
    modalBody: {
      width: 470,
      padding: 10,
      border: "none",
    },
  }),
  { name: "transfer-fund-form-styles" }
);

 export const NewWithdrawRequest = (props: NewWithdrawRequestProps) => {
  const classes = useStyles(props);
  const intl = useIntl();
  //context
  const dispatch = useContext(ICIPAdminDispatchContext);

  // Props
  const { business, isOpened, callback } = props;
  // State
  const [selectedAccount, setSelectedAccount] = useState((): Account => null);

  const [showInfoMessage, setShowInfoMessage] = useState(false);

  const [alertMessageContent, setAlertMessageContent] = useState('');

  const [isWarningMessage, setIsWarningMessage] = useState(false);

  const [showValidateBtn, setShowValidateBtn] = useState(false);

  const [showNextBtn, setShowNextBtn] = useState(true);

  // Events

  const isSelectedAccount = (account: Account) => {
    return selectedAccount != null && selectedAccount.id === account.id;
  };

  const selectAccount = (account: Account) => {
    
    setShowNextBtn(true);

    setShowValidateBtn(false);

    setShowInfoMessage(false);

    if (!formik.isSubmitting) {
      setSelectedAccount(account);
    }
  };

  //
  const getWithdrawalCost = (businessId: number) => {
    dispatch({
      type: ICIPAdminActionTypeEnum.loading,
    });

    BusinessService.getWithdrawCost(formik.values.amount, businessId, selectedAccount.id).then((response) => { 
      afterGetWithdrawCostIsHandle(response);
    });
  };

  const afterGetWithdrawCostIsHandle = (
    response: RestApiResponse<BaseResponseAttributes>
  ) => {
    if (response.isSuccess) {
      switch (response.data.code) {
        case 100:
          let result = JSON.parse(JSON.stringify(response.data.data));
          let cost = result.cost;
          let currencyCode = result.currencyCode;
          let totalAmount =  result.total;

          setIsWarningMessage(false);
          setAlertMessageContent(
            `
              ${intl.formatMessage({ id: "sharedScreen.cost" })}: 
              ${cost} ${currencyCode}
              -
              ${intl.formatMessage({ id: "sharedScreen.total" })}: 
              ${totalAmount} ${currencyCode}
            `
          );

          setShowInfoMessage(true);

          setShowNextBtn(false);

          setShowValidateBtn(true);
            
          break;
        case 101:
         
          setIsWarningMessage(true);
          setAlertMessageContent(intl.formatMessage({ id: "error.unknownError" }));
          setShowInfoMessage(true);

          break;
        case 102:

          setIsWarningMessage(true);
         setAlertMessageContent(intl.formatMessage({ id: "error.noWithdrawTarificationFound" }));
         setShowInfoMessage(true);
         break;
      }
    } else {
        setIsWarningMessage(true);
        setAlertMessageContent(intl.formatMessage({ id: "error.unknownError" }));
        setShowInfoMessage(true);
    }
  };



  const actionStateNextBtn = () => {
    return (
      selectedAccount != null &&
      formik.values.amount.length !== 0 &&
      !formik.isSubmitting
    );
  };

  // Handle formik

  const initFormValues = () => {
    return {
      amount: "",
    };
  };

  const formValidation = () => {
    return {
      amount: Yup.string().required(intl.formatMessage({ id: "error.amount" })),
    };
  };

  const formik = useFormik({
    initialValues: initFormValues(),
    validationSchema: Yup.object().shape({
      ...formValidation(),
    }),
    onSubmit: async (
      values: any,
      { setErrors, setSubmitting }: FormikHelpers<any>
    ) => {
      try {
        const response = await BusinessService.businessWithdrawRequest(
          business.id,
          selectedAccount.wallet_type,
          values.amount
        );
        setSubmitting(false);

        if (response.isSuccess) {
          switch (response.data.code) {
            case 100: // Ok
              let accounts: Array<Account> = [];

              JSON.parse(JSON.stringify(response.data.data)).forEach(
                (item, index) => {
                  accounts.push(Account.fromJson(item));
                }
              );

              callback(true);
              break;
            case 150:
              // Validation error
              setErrors(JSON.parse(JSON.stringify(response.data.data)));
              break;
            default:
              setErrors({ error: response.data.message });
              setIsWarningMessage(true);
              break;
          }
        } else {
          setErrors({
            unknown: intl.formatMessage({ id: "error.unknownError" }),
          });
        }
      } catch (error) {
        setErrors({
          unknown: intl.formatMessage({ id: "error.unknownError" }),
        });
      }
    },
  });

  const amountChange = (event: any) => {
    
    setShowNextBtn(true);

    setShowValidateBtn(false);

    setShowInfoMessage(false);

    let amount: string = event.currentTarget.value;

    if (amount.length !== 0 && !Number(amount)) return;
    formik.setFieldValue("amount", amount);
  };

  const genericError = () => {
    if (formik.errors.unknown) {
      return formik.errors.unknown as string;
    }

    if (formik.errors.amount) {
      return formik.errors.amount as string;
    }

    if (formik.errors.user_id) {
      return formik.errors.user_id as string;
    }

    if (formik.errors.wallet_type) {
      return formik.errors.wallet_type as string;
    }

    if (formik.errors.error) {
      return formik.errors.error as string;
    }

    return "";
  };

  return (
    <Modal
    className={classes.modal}
    open={isOpened}
    onClose={() => callback(true)}
  >
    <Paper className={classes.modalBody}>
      <PageTitle
        withAppTitle={false}
        title={`${intl.formatMessage({
          id: "sharedScreen.admin",
        })} - ${intl.formatMessage({
          id: "sharedScreen.newWithdrawRequest",
        })}`}
      />
      <Box className={classes.root}>
        <Container maxWidth="sm" className={classes.wrapper}>
          <Box component="div" mb={2}>
            <Typography variant="h4" component="h4">
              <FormattedMessage
                id="sharedScreen.withdrawRequest"
                values={{ name: business.company_name }}
              />
            </Typography>
            <Box component="span" className={classes.subTitle}>
              <FormattedMessage id="sharedScreen.selectWalletFrom" />
            </Box>
          </Box>
          <Box component="div" className={classes.walletItems}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Box
                  className={clsx(classes.walletItem, {
                    [classes.walletItemCurrent]: isSelectedAccount(
                      business.accounts[0]
                    ),
                  })}
                  onClick={() => selectAccount(business.accounts[0])}
                >
                  <AmountDetail
                    title={
                      <FormattedMessage
                        id={displayWalletType(business.accounts[0].wallet_type)}
                      />
                    }
                    subTitle={
                      <CurrencyFormat
                        value={business.accounts[0].balance}
                        suffix={` ${business.accounts[0].currency.code.toUpperCase()}`}
                        displayType="text"
                        thousandSeparator={true}
                        fixedDecimalScale={true}
                        decimalScale={2}
                        renderText={(formattedValue: string) => (
                          <Box component="span">{formattedValue}</Box>
                        )}
                      />
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box
                  className={clsx(classes.walletItem, {
                    [classes.walletItemCurrent]: isSelectedAccount(
                      business.accounts[1]
                    ),
                  })}
                  onClick={() => selectAccount(business.accounts[1])}
                >
                  <AmountDetail
                    title={
                      <FormattedMessage
                        id={displayWalletType(business.accounts[1].wallet_type)}
                      />
                    }
                    subTitle={
                      <CurrencyFormat
                        value={business.accounts[1].balance}
                        suffix={` ${business.accounts[1].currency.code.toUpperCase()}`}
                        displayType="text"
                        thousandSeparator={true}
                        fixedDecimalScale={true}
                        decimalScale={2}
                        renderText={(formattedValue: string) => (
                          <Box component="span">{formattedValue}</Box>
                        )}
                      />
                    }
                  />
                </Box>
              </Grid>
            </Grid>
            <Grid container spacing={3} className={classes.walletItemsGrid}>
              <Grid item xs={6}>
                <Box
                  className={clsx(classes.walletItem, {
                    [classes.walletItemCurrent]: isSelectedAccount(
                      business.accounts[2]
                    ),
                  })}
                  onClick={() => selectAccount(business.accounts[2])}
                >
                  <AmountDetail
                    title={
                      <FormattedMessage
                        id={displayWalletType(business.accounts[2].wallet_type)}
                      />
                    }
                    subTitle={
                      <CurrencyFormat
                        value={business.accounts[2].balance}
                        suffix={` ${business.accounts[2].currency.code.toUpperCase()}`}
                        displayType="text"
                        thousandSeparator={true}
                        fixedDecimalScale={true}
                        decimalScale={2}
                        renderText={(formattedValue: string) => (
                          <Box component="span">{formattedValue}</Box>
                        )}
                      />
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box
                  className={clsx(classes.walletItem, {
                    [classes.walletItemCurrent]: isSelectedAccount(
                      business.accounts[3]
                    ),
                  })}
                  onClick={() => selectAccount(business.accounts[3])}
                >
                  <AmountDetail
                    title={
                      <FormattedMessage
                        id={displayWalletType(business.accounts[3].wallet_type)}
                      />
                    }
                    subTitle={
                      <CurrencyFormat
                        value={business.accounts[3].balance}
                        suffix={` ${business.accounts[3].currency.code.toUpperCase()}`}
                        displayType="text"
                        thousandSeparator={true}
                        fixedDecimalScale={true}
                        decimalScale={2}
                        renderText={(formattedValue: string) => (
                          <Box component="span">{formattedValue}</Box>
                        )}
                      />
                    }
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box component="div" mt={4}>
            <InputField
              fullWidth
              id="amount"
              name="amount"
              label={intl.formatMessage({
                id: "sharedScreen.howManyFundWouldYouWithdraw"
              })}
              placeholder={""}
              type="text"
              endAdornment={
                <Box component="span" className={classes.currency}>
                  {business.business_currency.code.toString().toUpperCase()}
                </Box>
              }
              error={
                Boolean(formik.touched.amount) &&
                Boolean(formik.errors.amount) &&
                Boolean(formik.errors)
              }
              disabled={formik.isSubmitting}
              value={formik.values.amount}
              onChange={amountChange}
            />
          </Box>
          <Box mt={2} component="div" className={classes.error}>
            {genericError()}
          </Box>
          <Box component="div" margin={2}>
            {
              showInfoMessage && (
                <Alert severity={isWarningMessage ? 'warning':'info'}>
                  <AlertTitle>{alertMessageContent}</AlertTitle>
                </Alert>
              )
            }
          </Box>

          <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              mt={6}
            >
              {
                showNextBtn && (
                  <Box ml={3} component="div">
                    <InputButton
                      color="secondary"
                      fullWidth
                      size="medium"
                      variant="contained"
                      type="button"
                      onClick={() => getWithdrawalCost(business.id)} 
                      disabled={!actionStateNextBtn()}
                    >
                      <FormattedMessage id="sharedScreen.next" />
                    </InputButton>
                  </Box>
                )
              }
              
              {
                showValidateBtn && (
                  <Box ml={3} component="div">
                    <InputButton
                      color="secondary"
                      fullWidth
                      size="medium"
                      variant="contained"
                      type="submit"
                    >
                      <FormattedMessage id="sharedScreen.validate" />
                    </InputButton>
                  </Box>
                )
              }

              {formik.isSubmitting ? (
                <Box display="inline-block" className={classes.loader}>
                  <CircularProgress color="secondary" size={20} thickness={4} />
                </Box>
              ) : (
                <Box ml={3} component="div">
                  <OutlineButton onClick={() => callback(false)}  disabled={formik.isSubmitting}>
                    <FormattedMessage id="sharedScreen.cancel" />
                  </OutlineButton>
                </Box>
              )}
            </Box>
          </form>
        </Container>
      </Box>
      </Paper>
    </Modal>
  );
};
